import { get, isEmpty, isNil } from "lodash";
import { useState } from "react";
import { DEFAULT_DOCUMENT_STEP_FLAGS } from "utils/helpers/documentStepsConfig";
import { readFileAsBase64 } from "utils/helpers/files";
import { PDF_CONTENT_TYPE } from "../constants";
import { completeUsages } from "./StaticUsageLine";

const PDF_EXTENSION = /\.pdf$/i;

const usageValues = (usage) => ({
  key: usage.id,
  id: usage.id,
  insurancePlanIds: (get(usage, "insurancePlans") || []).map(({ id }) => id),
  ignoreOthers: Boolean(get(usage, "ignoreOthers")),
  exactMatch: Boolean(get(usage, "exactMatch")),
  steps: { ...DEFAULT_DOCUMENT_STEP_FLAGS, ...get(usage, "steps") },
});

const initialValues = (document) => ({
  name: get(document, "name") || "",
  type: get(document, "type") || null,
  usages: (get(document, "usages") || []).map(usageValues),
});

const useStaticDocumentForm = ({ document, fileRequired }) => {
  const [values, setValues] = useState(() => initialValues(document));
  const [file, setFile] = useState(null);

  const set = (key) => (value) =>
    setValues((current) => ({ ...current, [key]: value }));

  const pick = async (raw) => {
    setFile({
      content: await readFileAsBase64(raw),
      name: raw.name,
      contentType: raw.type || PDF_CONTENT_TYPE,
    });
    setValues((current) =>
      isEmpty(current.name.trim())
        ? { ...current, name: raw.name.replace(PDF_EXTENSION, "") }
        : current,
    );
    return false;
  };

  const isValid =
    !isEmpty(values.name.trim()) &&
    !isNil(values.type) &&
    (!fileRequired || !isNil(file));

  const data = () => ({
    name: values.name.trim(),
    type: values.type,
    usages: completeUsages(values.usages).map(
      ({ id, insurancePlanIds, ignoreOthers, exactMatch, steps }) => ({
        id,
        ignoreOthers,
        exactMatch,
        steps,
        insurancePlans: insurancePlanIds.map((planId) => ({ id: planId })),
      }),
    ),
    ...(file ? { file } : {}),
  });

  return { values, set, file, pick, isValid, data };
};

export default useStaticDocumentForm;
